import { useState } from 'react';
import useWebSocket, { ReadyState } from 'react-use-websocket';
import { DiscadorContextProviderProps } from '../props';
import { DiscadorContext } from './discadorContext';

import {
  DadosChamadaType,
  DadosDeslogarType,
  DadosDespausarType,
  DadosLogarType,
  DadosPausarType,
  DadosRespostaFazerChamadaType,
} from '../types';
import {
  mapperDadosChamada,
  mapperDadosDeslogar,
  mapperDadosDespausar,
  mapperDadosLogar,
  mapperDadosPausar,
  mapperDadosRespostaFazerChamada,
} from '../mappers';
import {
  DADOS_CHAMADA,
  DESTINO_RINGANDO,
  FIM_CHAMADA,
  RESPOSTA_FAZER_CHAMADA,
  RESULT_DESLOGAR,
  RESULT_DESPAUSAR,
  RESULT_LOGAR,
  RESULT_PAUSAR,
} from '../constants';
import { useHibrido } from '../../../context/HibridoContext';

export function DiscadorProvider({ children, socketUrl, user, password }: DiscadorContextProviderProps) {
  const { sendMessage, readyState, getWebSocket } = useWebSocket(socketUrl, {
    onMessage: onMessage,
    heartbeat: true,
    shouldReconnect: (closeEvent) => true,
    reconnectInterval: 3000,
    share: true
  });

  const [dadosLogin, setDadosLogin] = useState<DadosLogarType | null>(null);
  const [dadosChamada, setDadosChamada] = useState<DadosChamadaType | null>(null);
  const [chamandoDestino, setChamandoDestino] = useState<boolean>(false);
  const [operadorEmPausa, setOperadorEmPausa] = useState(false);
  // const { setDadosChamada: setDados } = useHibrido();

  const isLogged = !!dadosLogin;
  const onCall = !!dadosChamada;
  const onRinging = !!chamandoDestino;
  const isConnected = ReadyState.OPEN === readyState;
  const onPause = operadorEmPausa;

  function desconectarWS() {
    getWebSocket()?.close()
  }

  function onMessage(event: MessageEvent<string>): void {
    const [funcao, dados] = event.data.replace(')', '').split('(');

    switch (funcao.toLowerCase()) {
      case RESULT_LOGAR:
        const dadosLogarPayload = mapperDadosLogar(dados);
        onDadosLogar(dadosLogarPayload);
        break;

      case RESULT_DESLOGAR:
        const dadosDeslogarPayload = mapperDadosDeslogar(dados);
        onDadosDeslogar(dadosDeslogarPayload);
        break;

      case RESULT_PAUSAR:
        const dadosPausarPayload = mapperDadosPausar(dados);
        onDadosPausar(dadosPausarPayload);
        break;

      case RESULT_DESPAUSAR:
        const dadosDespausarPayload = mapperDadosDespausar(dados);
        onDadosDespausar(dadosDespausarPayload);
        break;

      case RESPOSTA_FAZER_CHAMADA:
        const dadosRespostaFazerChamadaPayload = mapperDadosRespostaFazerChamada(dados);
        onDadosRespostaFazerChamada(dadosRespostaFazerChamadaPayload);
        break;

      case DESTINO_RINGANDO:
        onDadosRingando();
        break;

      case DADOS_CHAMADA:
        const dadosChamadaPayload = mapperDadosChamada(dados);
        onDadosChamada(dadosChamadaPayload);
        // setDados(dadosChamadaPayload);
        break;

      case FIM_CHAMADA:
        onDadosFimChamada();
        break;
    }
  }

  function onDadosFimChamada() {
    setDadosChamada(null);
    setChamandoDestino(false);
  }

  function onDadosRingando() {
    setChamandoDestino(true);
  }

  function onDadosRespostaFazerChamada(dados: DadosRespostaFazerChamadaType) {
    if (dados.idRetorno === '0') window.alert(dados.descRetorno);
  }

  function onDadosLogar(dados: DadosLogarType): void {
    if (dados.idRetorno === '1') setDadosLogin(dados);
  }

  function onDadosDeslogar(data: DadosDeslogarType): void {
    setDadosLogin(null);
  }

  function onDadosPausar(data: DadosPausarType) {
    setOperadorEmPausa(true);
  }

  function onDadosDespausar(data: DadosDespausarType) {
    setOperadorEmPausa(false);
  }

  function onDadosChamada(data: DadosChamadaType) {
    setDadosChamada(data);
    setChamandoDestino(false);
  }

  function iniciarSessao(grupo: string = ''): void {
    sendMessage(`Logar(${user};${password};${grupo})`);
  }

  function iniciarPausa(user: string, pauseId: string, pause: string, group: string = '0'): void {
    sendMessage(`Pausar(${user};${group};${pauseId};${pause})`);
  }

  function iniciarChamada(
    usuario: string,
    ddd: string,
    telefone: string,
    grupo: string = '',
    contrato: string = '',
    identificador: string = ''
  ): void {
    sendMessage(`Discar(${identificador};${ddd};${telefone};;;${grupo};${contrato};DAC;;${usuario};)`);
  }

  function finalizarSessao(grupo: string = ''): void {
    sendMessage(`Deslogar(${user};${grupo})`);
  }

  function finalizarChamada(grupo: string = '') {
    sendMessage(`Desligar(${user};${grupo})`);
  }

  function finalizarPausa(group: string = '0'): void {
    sendMessage(`Despausar(${user};${group})`);
  }

  return (
    <DiscadorContext.Provider
      value={{
        iniciarSessao,
        finalizarChamada,
        finalizarPausa,
        finalizarSessao,
        iniciarChamada,
        iniciarPausa,
        isConnected,
        isLogged,
        onCall,
        onPause,
        onRinging,
        dadosLogin,
        dadosChamada,
        desconectarWS
      }}
    >
      {children}
    </DiscadorContext.Provider>
  );
}
